import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TSIconModule } from '../../lib/ts-icon';
import { NgClass, NgStyle } from '@angular/common';
import { TsOverlayComponent, TsOverlayModule } from '../../lib/ts-overlay';
import { ColorPickerDirective, ColorPickerModule } from 'ngx-color-picker';
import { TsSkeletonComponent } from '../../lib/ts-skeleton/ts-skeleton.component';
import { TsColorBubbleComponent } from '../../lib/ts-color-bubble';
import { TsButtonModule } from '../../lib/ts-button';
import { TsColorUtils } from '../../utils/ts-color.utils';

export interface TsColorOption {
  id: number | string;
  label: string;
  hex_value: string;
}

@Component({
  selector: 'ts-color-popover',
  templateUrl: './ts-color-popover.component.html',
  styles: [
    `
      :host {
        @apply flex flex-row items-center;
      }
    `,
  ],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TsColorPopoverComponent,
      multi: true,
    },
  ],
  imports: [
    MatTooltipModule,
    TSIconModule,
    NgStyle,
    NgClass,
    TsOverlayModule,
    ColorPickerModule,
    TsSkeletonComponent,
    TsColorBubbleComponent,
    TsButtonModule,
  ],
})
export class TsColorPopoverComponent implements ControlValueAccessor {
  @Input({ required: true }) colorOptions: TsColorOption[];
  @Input() selectedColorHex = '#fff';
  @Input() loading = false;
  @Input() enableAdd = false;
  /*
   * If true, the user will be able to add their own button to trigger the popover overlay.
   */
  @Input() detachedMode = false;

  @Output() newColorSelected = new EventEmitter<TsColorOption>();

  @ViewChild('overlay', { read: TsOverlayComponent }) overlay: TsOverlayComponent;
  @ViewChild(ColorPickerDirective) colorPicker: ColorPickerDirective;

  readonly controlContainer = inject(ControlContainer);

  fallbackColor = '#E6E6E6';

  get isContrastFromSelectColorLight() {
    return TsColorUtils.isContrastLight(this.value?.hex_value ?? this.fallbackColor);
  }

  value: TsColorOption | null = null;

  onChange = (value: TsColorOption | null) => {};
  onTouched = () => {};

  writeValue(value: TsColorOption | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: TsColorOption | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  selectColor(color: TsColorOption): void {
    this.value = color;
    this.onChange(this.value);
    this.onTouched();
  }

  displayOverlay(event) {
    this.overlay.displayOverlay(event);
  }

  onColorSelected(hexCode: string) {
    this.newColorSelected.emit({
      id: null, // new colors don't have an id
      label: hexCode, // use hexCode as label
      hex_value: hexCode,
    });
  }

  openColorPicker() {
    this.colorPicker.openDialog();
  }

  reset() {
    this.controlContainer.reset();
  }
}
